/* global KeyboardEvent */
/** Фокус модального окна (окно графика строки P&L, боковая панель карточки МП и ОПТ):
 *  при открытии — на `initialRef` (крестик), при закрытии — обратно на то, что было активно
 *  (иконка, по которой открыли); Esc закрывает; Tab/Shift+Tab не уходят из окна под подложку. */
import { useEffect, type RefObject } from "react";

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

let restoring = false;
/** Идёт возврат фокуса после закрытия окна (программный focus()) — всплывашки по фокусу
 *  (ProductHover) в этот момент не открываются. */
export function isRestoringDialogFocus(): boolean {
  return restoring;
}

export function useDialogFocus(
  dialogRef: RefObject<HTMLElement | null>,
  initialRef: RefObject<HTMLElement | null>,
  onClose: () => void,
): void {
  useEffect(() => {
    const returnTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    initialRef.current?.focus();
    return () => {
      if (!returnTo?.isConnected) return;
      restoring = true;
      try {
        returnTo.focus();
      } finally {
        restoring = false;
      }
    };
    // только при открытии/закрытии окна
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab" || !dialogRef.current) return;
      const items = [...dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)];
      if (items.length === 0) return;
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      const inside = dialogRef.current.contains(active);
      if (e.shiftKey && (!inside || active === first)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (!inside || active === last)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [dialogRef, onClose]);
}
